import React from 'react';
import { Text, Bold } from '@latitude/text';
import { Vertical } from '@latitude/spacing';
import { MARGIN } from '@latitude/core/utils/constants';
import { Link } from '@latitude/link';
import { getUrlFromEnv } from '../../utils/getUrlFromEnvUtil';

const oscUrl = getUrlFromEnv('funnel-osc');
const faqsCredit = {
  gemCreditLine: [
    {
      groupName: 'Purchases',
      groupData: [
        {
          title: 'Can I use my Gem CreditLine card for EFTPOS purchases?',
          content: (
            <Text>
              Yes, you can make purchases as long as you have available credit.
              You just need a PIN (Personal Identification Number). Create or
              personalise your PIN in any Westpac NZ branch. Please remember to
              carry a valid identification document (NZ Driver Licence or a
              Passport) with your Gem CreditLine card.
            </Text>
          )
        },
        {
          title: 'How do I keep track of all the transactions I’ve made?',
          content: (
            <Text>
              Each month, we’ll send you a statement that shows every
              transaction you’ve made. So it’s easy to keep on top of things.
              You can also manage your account and view your transactions online
              through the <Link href={oscUrl}>Latitude Service Centre</Link>.
            </Text>
          )
        },
        {
          title: 'Will I get finance promotion offers when I pay with EFTPOS?',
          content: (
            <Text>
              Yes, as long as it's a participating retailer. It's called
              'CreditLine120'. Whenever you spend more than $100 EFTPOS, you
              automatically get 120 days interest free and deferred payments.
            </Text>
          )
        },
        {
          title: 'When does my finance promotion offer start?',
          content: (
            <Text>
              Once you’ve got your goods and we have received all the correct
              documentation from the retailer, your purchase amount will be
              debited to your account. The date your purchase amount is debited
              is the start date of your finance promotion.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Your Credit Limit',
      groupData: [
        {
          title: 'How do I know what my credit limit is?',
          content: (
            <Text>
              Your credit limit can be found on your monthly Gem CreditLine
              statement, or you can call Customer Services on
              <Bold> 0800 500 505 </Bold>to ask us. You can also register with
              our <Link href={oscUrl}>Latitude Service Centre</Link> for 24/7
              access to your account.
            </Text>
          )
        },
        {
          title: 'Can I use my available credit for cash instead of purchases?',
          content: (
            <Text>
              Yes. Just remember that cash withdrawals will incur interest from
              the date of your purchase or withdrawal. As long as you have a PIN
              number for your card, you can withdraw cash from ATMs, EFTPOS
              terminals and Westpac branches throughout New Zealand. You can
              create or personalise your PIN in any Westpac NZ branch. Please
              remember to carry a valid identification document (NZ Driver
              Licence or a Passport) with your Gem CreditLine card.
            </Text>
          )
        },
        {
          title: 'What happens if I go over my credit limit?',
          content: (
            <Text>
              We understand life can be unpredictable, so if you're struggling
              with your finances the first thing to do is contact us. We will be
              able to advise what can be done to assist, which (depending on
              your circumstances) may include rescheduling your payments, or
              re-writing your contract for easier terms. If you prefer, we can
              refer you to the New Zealand Federation of Family Budgeting
              Services, which provides its services free of charge. Please call
              us on <Bold> 0800 003 943 </Bold> FREE for assistance.
            </Text>
          )
        },
        {
          title: 'How do I apply for a credit limit increase?',
          content: (
            <Text>
              We are able to review your credit limit every 6 months. To see if
              you're eligible for a credit limit increase, give us a call on
              <Bold> 0800 500 505 </Bold> to discuss a solution to suit you.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing your account',
      groupData: [
        {
          title:
            'What happens if I only make the minimum monthly payment each month?',
          content: (
            <Text>
              Each month, we only ask you to pay 3% of your outstanding balance
              or $20 (whichever is greater). If you only pay the minimum monthly
              payment, you may not pay it all off in that time. You'll then pay
              the prevailing interest rate on whatever's left.
            </Text>
          )
        },
        {
          title: 'Can I access my Gem CreditLine statements online?',
          content: (
            <Text>
              Yes, you can access your account 24/7 and view your statements
              online through the through the{' '}
              <Link href={oscUrl}>Latitude Service Centre</Link> If you're not
              yet registered,&nbsp;
              <Link href="https://servicecentre.latitudefinancial.co.nz">
                register now.
              </Link>
            </Text>
          )
        },
        {
          title: 'How do I change my Gem CreditLine card to a Gem Visa?',
          content: (
            <Text>
              Gem CreditLine is no longer available to new customers. It’s been
              replaced by Gem Visa, which offers the same benefits of Gem
              CreditLine just with more interest free options for you to choose
              from. If you would like to change to a Gem Visa, give us a call on
              <Bold> 0800 500 505 </Bold> to discuss your options.
            </Text>
          )
        },
        {
          title: 'How do I pay my Gem CreditLine account?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                There are a number of payment methods available. Choose the most
                convenient way for you to make repayments:
              </Text>
              <Text>{'\u2022'} Internet banking</Text>
              <Text>
                {'\u2022'} Direct Debit - you can download a direct debit form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/direct-debit/creditline-nz-ddr.pdf">
                  here.
                </Link>
              </Text>
              <Text>
                {'\u2022'} Automatic Payment - download an Automatic Payment
                form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/auto-payment/creditlinenz-ap.pdf">
                  here.
                </Link>
              </Text>
              <Text>{'\u2022'} Telephone banking</Text>
            </Vertical>
          )
        }
      ]
    }
  ],
  gemEssential: [
    {
      groupName: 'Your Credit Limit',
      groupData: [
        {
          title: 'How do I know what my credit limit is?',
          content: (
            <Text>
              Your credit limit can also be found on the top right of your
              monthly Gem Essential statement. Alternatively, you can log onto
              the&nbsp;
              <Link href="https://servicecentre.latitudefinancial.co.nz">
                Latitude Service Centre
              </Link>
              &nbsp;to check it any time. It's important to note that your
              credit limit can be different from your available cash limit. Your
              available cash limit can be found under 'Account Summary' in the
              Centre. It’s also available on your statement.
            </Text>
          )
        },
        {
          title: 'How do I apply for a credit limit increase?',
          content: (
            <Text>
              To see if you’re eligible for a credit limit increase, give us a
              call on <Bold> 0800 500 505 </Bold>
            </Text>
          )
        },
        {
          title: 'What happens if I go over my credit limit?',
          content: (
            <Text>
              We understand life can be unpredictable, so if you’re struggling
              with your finances the first thing to do is contact us. If you
              prefer, we can refer you to the New Zealand Federation of Family
              Budgeting Services, which provides its services free of charge.
              Please call Gem on 0800 500 505 if you wish to discuss further
              assistance.
            </Text>
          )
        },
        {
          title: 'How much cash can I withdraw on my Gem Essential card?',
          content: (
            <Text>
              Subject to your available credit and cash limit, you can withdraw
              up to $1,000 per day at an ATM and up to $3,000 per day over the
              counter at a financial institution. (Please note, this may be less
              depending on where you are in the world. Some financial
              institutions may limit the amount of cash you can withdraw through
              their facilities). The details of your cash limit can be found on
              your latest statement or by logging on to the&nbsp;
              <Link href="https://servicecentre.latitudefinancial.co.nz">
                Latitude Service Centre
              </Link>
            </Text>
          )
        },
        {
          title: 'How do I change my credit limit?',
          content: (
            <Text>
              Call our Customer Service team on 0800 500 505 to request an
              increase to your credit limit and if approved, your increase will
              take effect immediately. If your personal circumstances have
              changed, and you wish to decrease your credit limit, then please
              call us.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing your account',
      groupData: [
        {
          title: 'How do I make payments toward my Gem Essential card?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                There are a number of payment methods available. Choose the most
                convenient way for you to make repayments:
              </Text>
              <Text>
                {'\u2022'} Internet banking (using the same payment details for
                your previous card).
              </Text>
              <Text>{'\u2022'} Direct Debit</Text>
              <Text>{'\u2022'} Automatic Payment</Text>
              <Text>
                Payments to your Gem Essential card will not be accepted at any
                Countdown stores.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'How much do I have to repay each month?',
          content: (
            <Text>
              Each month you need to pay at least 3% of your outstanding balance
              or $20 (whichever is greater). You can pay more if you want to.
            </Text>
          )
        },
        {
          title:
            "When should I pay my account to ensure it's processed by the due date?",
          content: (
            <Text>
              To ensure your payment is received by the due date please pay by
              your preferred payment method by the due date if it falls on a
              business day or last business day before your due date. Payments
              made before 3pm NZST on business days will be processed on the
              same business day. Please allow up to three business days for the
              payment to show in your transaction history.
            </Text>
          )
        },
        {
          title: 'How do I download the Latitude Financial Services App?',
          content: (
            <Text>
              Go into your app store and download the Latitude Financial
              Services App.
            </Text>
          )
        },
        {
          title:
            'Can I pay my account using the Latitude Financial Services App?',
          content: (
            <Text>
              No, in New Zealand you are unable to pay your account using the
              Latitude Financial Services App, however, it does provide you with
              the payment details to do so.
            </Text>
          )
        },
        {
          title: 'Can anyone else apply for the Gem Essential card?',
          content: (
            <Text>
              We will not be accepting applications for new accounts. Only
              existing customers (and their existing and future additional
              cardholders) who have qualified for Gem Essential, will be able to
              receive Gem Essential credit cards.
            </Text>
          )
        }
      ]
    }
  ],
  gemVisaCard: [
    {
      groupName: 'Application & activation',
      groupData: [
        {
          title: 'What is the approval criteria for Gem Visa?',
          content: (
            <Text>
              As a responsible lender, we are required to assess your financial
              circumstances to ensure the credit is affordable and meets your
              needs. We are also required to verify your identity and address.
              To be eligible, you must be at least 18 years of age and a
              citizen, permanent resident of New Zealand or eligible to reside
              in New Zealand for 12 months or more.
            </Text>
          )
        },
        {
          title: 'When will I get a decision on my application?',
          content: (
            <Text>
              Once you’ve completed your application and submitted it we will
              provide a response (approved, declined, need more information)
              within 60 seconds. If we require more information, we’ll email you
              to let you know what we need. We’ll give you a call to follow up a
              few days later if we don’t hear from you.
            </Text>
          )
        },
        {
          title: 'Can I use my card straight away?',
          content: (
            <Text>
              You will need to activate your card before you start to use it.
              (This helps us ensure the right person has received your card).
              Don’t forget to sign your card immediately. To activate your card
              call <Bold> 0800 500 505 </Bold>. When you are activating your
              card, please follow the prompts to select your own Personal
              Identification Number (PIN). After this your card will be ready
              for use.
            </Text>
          )
        },
        {
          title: 'How do I activate my credit card?',
          content: (
            <Text>
              Choose your PIN&nbsp;
              <Link href="https://servicecentre.latitudefinancial.co.nz">
                here&nbsp;
              </Link>
              or call 0800 500 505 and follow the prompts. It only takes a
              couple of minutes.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Your Credit Limit',
      groupData: [
        {
          title: 'How do I change my credit limit?',
          content: (
            <Text>
              Call our Customer Solutions team on <Bold> 0800 003 943 </Bold> to
              request an increase to your credit limit and if approved, your
              increase will take effect immediately. If your personal
              circumstances have changed, and you wish to decrease your credit
              limit, then please call us.
            </Text>
          )
        },
        {
          title: 'What happens if I go over my credit limit?',
          content: (
            <Text>
              We understand life can be unpredictable, so if you’re struggling
              with your finances the first thing to do is contact us. We will be
              able to advise what can be done to assist, which (depending on
              your circumstances) may include rescheduling your payments, or
              re-writing your contract for easier terms. If you prefer, we can
              refer you to the New Zealand Federation of Family Budgeting
              Services, which provides its services free of charge. Please call
              us on <Bold> 0800 500 505 </Bold> FREE for assistance.
            </Text>
          )
        },
        {
          title: 'How much cash can I withdraw on my Gem Visa?',
          content: (
            <Text>
              Subject to your available credit and cash limit, you can withdraw
              up to $1,000 per day at an ATM and up to $3,000 per day over the
              counter at a financial institution. (Please note, this may be less
              depending on where you are in the world. Some financial
              institutions may limit the amount of cash you can withdraw through
              their facilities.) The details of your cash limit can be found on
              your latest statement or by logging on to the&nbsp;
              <Link href={oscUrl}>Latitude Service Centre.</Link>
            </Text>
          )
        },
        {
          title: 'How often can I apply for a credit limit increase?',
          content: (
            <Text>We are able to review your credit limit every 6 months.</Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing your account',
      groupData: [
        {
          title: 'What is the Modified Closing Balance',
          content: (
            <Text>
              For more information on the Modified Closing balance, please click{' '}
              <Link
                target="_blank"
                href="https://gemfinance.zendesk.com/hc/en-nz/articles/4403398925073-Credit-Card-Payment-FAQs"
              >
                here
              </Link>
              .
            </Text>
          )
        },
        {
          title:
            'What should I do if my credit card is lost, damaged or stolen?',
          content: (
            <Text>
              Our Lost and Stolen team is available 24/7 and will be able to
              assist you. Call the Customer Solutions team on{' '}
              <Bold>0800 500 505</Bold>. If you’re overseas call us on{' '}
              <Bold>+64 9 362 5432</Bold>.
            </Text>
          )
        },
        {
          title: 'How do I reset my Latitude Service Centre password?',
          content: (
            <Text>
              If you have forgotten your password and need to reset it, you can
              do this quickly online by clicking on the 'Forgot Password' link
              on the <Link href={oscUrl}>Latitude Service Centre</Link> login
              screen.
            </Text>
          )
        },
        {
          title: 'How do I pay my credit card account?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                There are a number of payment methods available. Choose the most
                convenient way for you to make repayments:
              </Text>
              <Text>{'\u2022'} Internet banking</Text>
              <Text>
                {'\u2022'} Direct Debit - you can download a direct debit form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/direct-debit/GemVisa_Direct_Debit.pdf">
                  here.
                </Link>
              </Text>
              <Text>
                {'\u2022'} Automatic Payment - download an Automatic Payment
                form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/auto-payment/gemvisanz-ap.pdf">
                  here.
                </Link>
              </Text>
              <Text>{'\u2022'} Telephone banking</Text>
              <Text>{'\u2022'} In person - at your local PostShop</Text>
              <Text>
                We will not treat a payment as being made until it is actually
                credited to your account. Payments for your account should be
                received by the due date listed on your statement. Because
                processing of payments is often delayed on weekends and public
                holidays, it’s best to make your payment at least one working
                day (ie between Monday to Friday) before it is due to avoid any
                additional charges.
              </Text>
            </Vertical>
          )
        }
      ]
    }
  ],
  gemVisaCardApple: [
    {
      groupName: 'Application & activation',
      groupData: [
        {
          title: 'What is the approval criteria for Gem Visa?',
          content: (
            <Text>
              As a responsible lender, we are required to assess your financial
              circumstances to ensure the credit is affordable and meets your
              needs. We are also required to verify your identity and address.
              To be eligible, you must be at least 18 years of age and a
              citizen, permanent resident of New Zealand or eligible to reside
              in New Zealand for 12 months or more.
            </Text>
          )
        },
        {
          title: 'When will I get a decision on my application?',
          content: (
            <Text>
              Once you’ve completed your application and submitted it we will
              provide a response (approved, declined, need more information)
              within 60 seconds. If we require more information, we’ll email you
              to let you know what we need. We’ll give you a call to follow up a
              few days later if we don’t hear from you.
            </Text>
          )
        },
        {
          title: 'Can I use my card straight away?',
          content: (
            <Text>
              You will need to activate your card before you start to use it.
              (This helps us ensure the right person has received your card).
              Don’t forget to sign your card immediately. To activate your card
              call <Bold> 0800 500 505 </Bold>. When you are activating your
              card, please follow the prompts to select your own Personal
              Identification Number (PIN). After this your card will be ready
              for use.
            </Text>
          )
        },
        {
          title: 'How do I activate my credit card?',
          content: (
            <Text>
              Choose your PIN&nbsp;
              <Link href="https://online.gemvisa.co.nz/access/login">
                here&nbsp;
              </Link>
              or call 0800 500 505 and follow the prompts. It only takes a
              couple of minutes.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Your Credit Limit',
      groupData: [
        {
          title: 'How do I change my credit limit?',
          content: (
            <Text>
              Call our Customer Solutions team on <Bold> 0800 500 505 </Bold> to
              request an increase to your credit limit and if approved, your
              increase will take effect immediately. If your personal
              circumstances have changed, and you wish to decrease your credit
              limit, then please call us.
            </Text>
          )
        },
        {
          title: 'What happens if I go over my credit limit?',
          content: (
            <Text>
              We understand life can be unpredictable, so if you’re struggling
              with your finances the first thing to do is contact us. We will be
              able to advise what can be done to assist, which (depending on
              your circumstances) may include rescheduling your payments, or
              re-writing your contract for easier terms. If you prefer, we can
              refer you to the New Zealand Federation of Family Budgeting
              Services, which provides its services free of charge. Please call
              us on <Bold> 0800 003 943 </Bold> FREE for assistance.
            </Text>
          )
        },
        {
          title: 'How much cash can I withdraw on my Gem Visa?',
          content: (
            <Text>
              Subject to your available credit and cash limit, you can withdraw
              up to $1,000 per day at an ATM and up to $3,000 per day over the
              counter at a financial institution. (Please note, this may be less
              depending on where you are in the world. Some financial
              institutions may limit the amount of cash you can withdraw through
              their facilities.) The details of your cash limit can be found on
              your latest statement or by logging on to the&nbsp;
              <Link href="https://online.gemvisa.co.nz/wps/myportal/gemnz">
                Online Service Centre.
              </Link>
            </Text>
          )
        },
        {
          title: 'How often can I apply for a credit limit increase?',
          content: (
            <Text>We are able to review your credit limit every 6 months.</Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing your account',
      groupData: [
        {
          title: 'What is the ‘Payment to Reduce Further Interest’?',
          content: (
            <Text>
              The ‘Payment to Reduce Further Interest’ amount will show how much
              you need to pay that month to reduce the amount of interest you’re
              charged in the future, or in some cases, avoid interest
              altogether. It includes the minimum monthly payment, any
              outstanding fees, non-promotional balances and any promotional
              transactions that will become interest bearing shortly. The
              ‘Payment to Reduce Further Interest’ is only a suggested payment
              to help you reduce the amount of interest you pay on your account.
              You must however pay the at least the minimum monthly payment by
              the payment due date.
            </Text>
          )
        },
        {
          title:
            'What should I do if my credit card is lost, damaged or stolen?',
          content: (
            <Text>
              Our Lost and Stolen team is available 24/7 and will be able to
              assist you. Call the Customer Solutions team on{' '}
              <Bold>0800 500 505</Bold>. If you’re overseas call us on{' '}
              <Bold>+64 9 362 5432</Bold>.
            </Text>
          )
        },
        {
          title: 'How do I reset my Online Service Centre password?',
          content: (
            <Text>
              If you have forgotten your password and need to reset it, you can
              do this quickly online.{' '}
              <Link href="https://online.gemvisa.co.nz/wps/myportal/gemnz">
                Recover your password
              </Link>
            </Text>
          )
        },
        {
          title: 'How do I pay my credit card account?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                There are a number of payment methods available. Choose the most
                convenient way for you to make repayments:
              </Text>
              <Text>{'\u2022'} Internet banking</Text>
              <Text>
                {'\u2022'} Direct Debit - you can download a direct debit form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/direct-debit/GemVisa_Direct_Debit.pdf">
                  here.
                </Link>
              </Text>
              <Text>
                {'\u2022'} Automatic Payment - download an Automatic Payment
                form{' '}
                <Link href="https://assets.latitudefinancial.com/forms/cards/auto-payment/gemvisanz-ap.pdf">
                  here.
                </Link>
              </Text>
              <Text>{'\u2022'} Telephone banking</Text>
              <Text>{'\u2022'} In person - at your local PostShop</Text>
              <Text>
                We will not treat a payment as being made until it is actually
                credited to your account. Payments for your account should be
                received by the due date listed on your statement. Because
                processing of payments is often delayed on weekends and public
                holidays, it’s best to make your payment at least one working
                day (ie between Monday to Friday) before it is due to avoid any
                additional charges.
              </Text>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'Shopping at Apple',
      groupData: [
        {
          title: 'Which Latitude cards are eligible at Apple?',
          content: (
            <Text>
              0% interest offers are available at Apple for Gem Visa and Gem
              CreditLine cards only. To place your order, call Apple{' '}
              <Bold>(0800 692 7753).</Bold>
            </Text>
          )
        },
        {
          title: 'What products are eligible for 0% interest?',
          content: (
            <Text>
              All products are eligible for 0% Interest when you spend $250 or
              more at Apple. This includes hardware, software, and accessories.
              iTunes and App Store purchases are excluded.
            </Text>
          )
        },
        {
          title: 'What restrictions apply to shopping at Apple?',
          content: (
            <Text>
              To be eligible for 0% Interest offers, you must call Apple{' '}
              <Bold>(0800 692 7753)</Bold> only. It is important to be aware
              that 0% Interest offers not available for purchases made in Apple
              Online Store or from Apple Resellers.
            </Text>
          )
        },
        {
          title: 'When can I start shopping 0% interest at Apple?',
          content: (
            <Text>
              You will be able to shop immediately. If you are approved, you
              will be notified (on-screen and by email).To place an order,
              simply call Apple on <Bold>0800 692 7753 </Bold>(Mon – Fri , 7am –
              7pm Sat, 7am – 4pm NZST)
            </Text>
          )
        }
      ]
    }
  ]
};

export default faqsCredit;
